// Sales tax on an estimate / quote (CEO 2026-10-13). Flow works the rate out
// from the ship-to address (POST /orders/:id/tax/calculate → Stripe Tax; a
// tax-exempt client is 0%).
//
// Stephanie 2026-10-14: "the sales tax line under the item should autopopulate.
// there is no need for having the box on the right" — so this renders nothing.
// It asks for the rate on its own: when there is a ship-to and the items have
// prices, and the rate is still blank or 0%. A rate worked out before any price
// was typed used to stick at 0%; the server now refuses to save that, and this
// asks again once prices are in. A new ship-to is re-worked by the server
// (PATCH /orders/:id).
import { useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import { api } from "@/lib/api";

export function SalesTaxAuto(props: {
  orderId: string;
  ratePct: string | null;
  /** "FL 33314" — empty when there is no usable ship-to. */
  shipToKey: string;
  /** At least one non-shipping line has a price. */
  hasPricedItems: boolean;
  detailQueryKey: QueryKey;
}) {
  const { orderId, ratePct, shipToKey, hasPricedItems, detailQueryKey } = props;
  const queryClient = useQueryClient();
  const calculate = useMutation({
    mutationFn: () => api(`/orders/${orderId}/tax/calculate`, { method: "POST" }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: detailQueryKey }),
  });

  // Once per ship-to + "items priced" state, so a 0% answer (tax-exempt
  // client, a state we don't collect in) isn't asked again on every render.
  const asked = useRef(new Set<string>());
  const needsRate = ratePct == null || Number(ratePct) === 0;
  useEffect(() => {
    if (!hasPricedItems || !needsRate || calculate.isPending) return;
    // No ship-to yet → the server uses the client's normal address (Stephanie 2026-11-02).
    const key = `${shipToKey || "client-default"}|priced`;
    if (asked.current.has(key)) return;
    asked.current.add(key);
    calculate.mutate();
  }, [shipToKey, hasPricedItems, needsRate, calculate]);

  return null;
}
